import { useEffect, useState } from 'react';
import { apiClient, ApiError, type InvitePeek, type MeResponse } from '@/lib/api/client';
import { useDialogFocus } from '@/lib/useDialogFocus';

interface Props {
  token: string;
  me: MeResponse | null;
  onAccepted: () => void;
  onDismiss: () => void;
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; peek: InvitePeek }
  | { kind: 'error'; message: string };

const ERROR_MAP: Record<string, string> = {
  invite_not_found: "This invite doesn't exist.",
  invite_consumed: 'This invite has already been used.',
  invite_expired: 'This invite has expired. Ask the inviter to send another.',
  invite_email_mismatch:
    "This invite was sent to a different email. Sign in with that address to accept.",
};

export function InviteAcceptModal({ token, me, onAccepted, onDismiss }: Props) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [accepting, setAccepting] = useState(false);
  const [pinWsId, setPinWsId] = useState<string>('');
  const panelRef = useDialogFocus<HTMLDivElement>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const peek = await apiClient.peekInvite(token);
        if (!cancelled) {
          setState({ kind: 'ready', peek });
          // Default pin target = the user's first workspace
          if (me?.workspaces[0]) setPinWsId(me.workspaces[0].ws_id);
        }
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof ApiError ? (ERROR_MAP[e.code] ?? e.message) : 'Failed to load invite.';
        setState({ kind: 'error', message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, me]);

  async function accept() {
    setAccepting(true);
    try {
      const isProject = state.kind === 'ready' && state.peek.target_type === 'project';
      await apiClient.acceptInvite(token, {
        pin_ws_id: isProject && pinWsId ? pinWsId : null,
      });
      onAccepted();
    } catch (e) {
      const msg =
        e instanceof ApiError ? (ERROR_MAP[e.code] ?? e.message) : 'Failed to accept invite.';
      setState({ kind: 'error', message: msg });
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onDismiss();
      }}
    >
      <div ref={panelRef} className="w-full max-w-md space-y-5 rounded-2xl border border-border bg-card p-7 shadow-2xl">
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {state.kind === 'ready' && state.peek.target_type === 'project'
              ? 'Project invite'
              : 'Workspace invite'}
          </div>
          {state.kind === 'loading' && (
            <h2 id="invite-title" className="text-base text-muted-foreground">Loading invite…</h2>
          )}
          {state.kind === 'ready' && (
            <>
              <h2 id="invite-title" className="text-2xl font-semibold tracking-tight">
                Join{' '}
                <span className="text-primary">
                  {state.peek.target_type === 'project'
                    ? (state.peek.project?.name ?? 'project')
                    : (state.peek.workspace?.name ?? 'workspace')}
                </span>
              </h2>
              <p className="text-sm text-muted-foreground">
                You'll join as <span className="font-mono text-foreground">{state.peek.role}</span>.
                Invite sent to{' '}
                <span className="font-mono text-foreground">{state.peek.invited_email}</span>.
              </p>
              {state.peek.target_type === 'project' && state.peek.workspace && (
                <p className="text-xs text-muted-foreground">
                  Lives in workspace{' '}
                  <span className="text-foreground">{state.peek.workspace.name}</span>.
                </p>
              )}
            </>
          )}
          {state.kind === 'error' && (
            <>
              <h2 id="invite-title" className="text-xl font-semibold tracking-tight">Can't accept</h2>
              <p className="text-sm text-muted-foreground">{state.message}</p>
            </>
          )}
        </div>
        {state.kind === 'ready' && state.peek.target_type === 'project' && me && (
          <div className="space-y-2 rounded-md border border-border bg-background/40 p-3">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              File this project under
            </label>
            <select
              value={pinWsId}
              onChange={(e) => setPinWsId(e.target.value)}
              className="block w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              <option value="">Don't pin — find via All Projects</option>
              {me.workspaces.map((w) => (
                <option key={w.ws_id} value={w.ws_id}>
                  {w.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground">
              You can change this later from All Projects.
            </p>
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={onDismiss}
            className="flex-1 rounded-md border border-border bg-background px-4 py-2.5 text-sm text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            {state.kind === 'error' ? 'Close' : 'Maybe later'}
          </button>
          {state.kind === 'ready' && (
            <button
              onClick={accept}
              disabled={accepting}
              className="flex-1 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {accepting ? 'Joining…' : 'Accept invite'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
